import React, { useEffect } from 'react';
import { View, Text, StyleSheet, FlatList, RefreshControl } from 'react-native';
import { router } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme, SPACING, BORDER_RADIUS, CONTENT_MAX_WIDTH, TYPOGRAPHY } from '../../src/theme';
import { EmptyState, Skeleton } from '@components/common';
import Head from 'expo-router/head';
import { Header } from '@components/layout';
import { useAuthStore } from '../../src/store/authStore';
import { useStructuresStore } from '../../src/store/structuresStore';
import { Structure, StructureClaim } from '../../src/types';
import { Home } from 'lucide-react-native';
import { PropertyListRow } from '@components/structures/PropertyListRow';

interface ClaimedProperty {
  structure: Structure;
  claim: StructureClaim;
}

export default function MyPropertiesScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { user } = useAuthStore();
  const { claims, claimedStructureIds, fetchClaimedStructures, fetchStructuresByIds, isLoading } =
    useStructuresStore();
  const [properties, setProperties] = React.useState<ClaimedProperty[]>([]);
  const [refreshing, setRefreshing] = React.useState(false);
  const [hasAttempted, setHasAttempted] = React.useState(false);

  // Resolve structure docs for claimed IDs
  React.useEffect(() => {
    async function resolve() {
      const idsWithClaims = claimedStructureIds.filter((id) => !!claims[id]);
      const structures = await fetchStructuresByIds(idsWithClaims);
      const results: ClaimedProperty[] = structures.flatMap((structure) => {
        const claim = claims[structure.id];
        return claim ? [{ structure, claim }] : [];
      });
      setProperties(results);
    }
    resolve();
  }, [claimedStructureIds, claims, fetchStructuresByIds]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetchClaimedStructures(user.id).finally(() => {
      if (!cancelled) setHasAttempted(true);
    });
    return () => {
      cancelled = true;
    };
  }, [user, fetchClaimedStructures]);

  const handleRefresh = async () => {
    if (!user) return;
    setRefreshing(true);
    await fetchClaimedStructures(user.id);
    setRefreshing(false);
  };

  // Show empty state only after the fetch has completed AND there are no claims.
  // Otherwise show the spinner — covers initial mount (before fetch starts) and
  // the gap between claims arriving and structure docs being resolved.
  const showEmpty = hasAttempted && !isLoading && claimedStructureIds.length === 0;

  // Hairline block, not an elevated card: a bare icon row, the shared
  // ReadinessMeter, and a metadata line — extracted to PropertyListRow so
  // PropertyPickerSheet can render the same rows with a role label added.
  const renderProperty = ({ item }: { item: ClaimedProperty }) => (
    <PropertyListRow
      structure={item.structure}
      claim={item.claim}
      onPress={() => router.push(`/structure/${item.structure.id}`)}
      testID="my-property-card"
    />
  );

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Head>
        <title>My Properties - Redi</title>
      </Head>
      <Header showBack showAvatar={false} />
      <FlatList
        data={properties}
        renderItem={renderProperty}
        keyExtractor={(item) => item.structure.id}
        contentContainerStyle={[
          styles.listContent,
          showEmpty && styles.emptyListContent,
          { paddingBottom: insets.bottom + 100 },
        ]}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
        ListHeaderComponent={
          <View style={styles.itemConstraint}>
            <Text style={[styles.screenTitle, { color: colors.text }]}>My Properties</Text>
          </View>
        }
        ListEmptyComponent={
          <View style={[styles.itemConstraint, showEmpty && styles.emptyFill]}>
            {showEmpty ? (
              <EmptyState
                icon={Home}
                title="No properties claimed"
                message="Claim a property from the map to track its compliance progress and documentation."
                actionLabel="Go to Map"
                onAction={() => router.push('/(tabs)')}
              />
            ) : (
              <>
                {[0, 1, 2].map((i) => (
                  <View key={i} style={[styles.propertyCard, { borderBottomColor: colors.border }]}>
                    <View style={styles.headerRow}>
                      <Skeleton
                        width={20}
                        height={20}
                        borderRadius={BORDER_RADIUS.sm}
                        style={{ marginRight: SPACING.smd }}
                      />
                      <View style={styles.propertyInfo}>
                        <Skeleton width="70%" height={16} />
                        <Skeleton width="45%" height={13} style={{ marginTop: 6 }} />
                      </View>
                    </View>
                    <Skeleton width="100%" height={72} borderRadius={BORDER_RADIUS.lg} />
                    <View style={styles.metaRow}>
                      <Skeleton width={72} height={13} />
                    </View>
                  </View>
                ))}
              </>
            )}
          </View>
        }
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  listContent: {
    paddingTop: SPACING.lg,
  },
  screenTitle: {
    ...TYPOGRAPHY.screenTitle,
    marginBottom: SPACING.md,
  },
  itemConstraint: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    alignSelf: 'center',
    paddingHorizontal: SPACING.md,
  },
  // Only grow — centring here would centre the list HEADER too, and the
  // header holds the screen title. That is what put "My Properties" halfway
  // down the page with the empty state's icon sitting on top of it (the
  // -50 marginTop that used to be on the empty state made the overlap worse).
  // The empty child fills the space under the title and centres inside it.
  emptyListContent: {
    flexGrow: 1,
  },
  emptyFill: {
    flex: 1,
    justifyContent: 'center',
  },
  propertyCard: {
    width: '100%',
    alignSelf: 'stretch',
    paddingVertical: SPACING.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  propertyInfo: {
    flex: 1,
  },
  // No rule of its own: the block is already hairline-bounded, and a second
  // divider inside it re-creates the card. (It also rendered pure black —
  // borderTopWidth with no borderTopColor falls back to RN's default.)
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: SPACING.smd,
    gap: SPACING.xs,
  },
});
